import React, { useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  DialogContent,
  DialogActions,
  TextField,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import api from "../../axiosInstance";
import { toast } from "react-toastify";

const ACCENT = "#c62828";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ACCENT} 0%, #e53935 100%)`;

const formatSlot = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })} · ${d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}`;
};

function MentorCancelBookingDialog({ open, booking, onClose, onSuccess }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setReason("");
  }, [open, booking]);

  const handleSubmit = async () => {
    if (!booking || !reason.trim()) return;
    setSaving(true);
    try {
      await api.post(`/admin/mentors/bookings/${booking.bookingId}/cancel`, {
        reason,
      });
      toast.success("Session cancelled successfully!");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.errorMessage ||
          "Failed to cancel session. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          background: HEADER_GRADIENT,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: "10px",
              background: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CancelIcon sx={{ color: "#fff", fontSize: 18 }} />
          </Box>
          <Typography
            sx={{ color: "#fff", fontWeight: 700, fontSize: "1.05rem" }}
          >
            Cancel Session
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon sx={{ color: "#fff", fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 3 }}>
        {booking && (
          <Box
            sx={{
              background: "#f5f7fb",
              borderRadius: "10px",
              px: 2,
              py: 1.2,
              mb: 2.5,
              textAlign: "left",
            }}
          >
            <Typography
              sx={{ fontSize: "0.7rem", color: "#8a94a6", fontWeight: 600 }}
            >
              SCHEDULED SESSION
            </Typography>
            <Typography
              sx={{ fontSize: "0.9rem", fontWeight: 700, color: ACCENT }}
            >
              {formatSlot(booking.slotStartTs)}
            </Typography>
          </Box>
        )}

        <TextField
          fullWidth
          size="small"
          label="Reason"
          placeholder="Enter reason for cancellation"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          multiline
          minRows={3}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5 }}>
        <Button
          onClick={onClose}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 600,
            color: "#616161",
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          disabled={saving || !reason.trim()}
          onClick={handleSubmit}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            background: HEADER_GRADIENT,
            boxShadow: "none",
          }}
        >
          {saving ? "Cancelling..." : "Cancel Session"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MentorCancelBookingDialog;
